/**
 * Single source of truth for the app's color palette. `sourse/theme.ts` derives
 * MUI's palette from this table; `sourse/styles/tokens.generated.css` (built by
 * `generateTailwindTokensCss`, wired into `vite.config.ts`) derives Tailwind's
 * `@theme` block from the same table. Neither file should hardcode a hex value
 * for a token defined here.
 *
 * Deliberately dependency-free (no `@` alias imports) — `vite.config.ts` imports
 * this module directly at Node/config-load time to generate CSS, before Vite's
 * own alias resolution is available.
 *
 * Editor-only tokens (`--color-chrome-*`, `--color-ink`, `--color-muted`, etc. in
 * `tailwind.css`) are NOT part of this table — they have no MUI counterpart and
 * are intentionally a separate, Tailwind-only vocabulary (see the comment above
 * them in `tailwind.css`).
 */

export type ThemeMode = 'light' | 'dark';

type ByMode<T> = Record<ThemeMode, T>;

export type CustomPaletteKey =
    | 'c1'
    | 'c2'
    | 'c3'
    | 'c4'
    | 'c5'
    | 'c6'
    | 'c7'
    | 'c8'
    | 'c9'
    | 'c10'
    | 'c11'
    | 'c12'
    | 'c13'
    | 'c14'
    | 'c15'
    | 'c16'
    | 'c17'
    | 'c18'
    | 'c19'
    | 'c20'
    | 'c21'
    | 'c22'
    | 'c23'
    | 'c24'
    | 'c25'
    | 'c26'
    | 'c27'
    | 'c28'
    | 'c29'
    | 'c30'
    | 'c31'
    | 'c32'
    | 'c33'
    | 'c34'
    | 'c35'
    | 'c36'
    | 'c37'
    | 'c38'
    | 'c39'
    | 'c40'
    | 'c41'
    | 'c42'
    | 'c43'
    | 'c44'
    | 'c45'
    | 'c46'
    | 'c47'
    | 'c48'
    | 'c49'
    | 'c50'
    | 'c51'
    | 'c52'
    | 'c53'
    | 'c54'
    | 'c55'
    | 'c56'
    | 'c57'
    | 'c58'
    | 'c59'
    | 'c60'
    | 'c61'
    | 'c62'
    | 'c63'
    | 'c64'
    | 'c65'
    | 'c66'
    | 'c67'
    | 'c68'
    | 'c69'
    | 'c70'
    | 'c71'
    | 'c72'
    | 'c73'
    | 'c74'
    | 'c75'
    | 'c76'
    | 'c77'
    | 'c78'
    | 'c79'
    | 'c80';

/** Minted semantic tokens (ADR 0018 mode-flip audit) — role names, never a new cXX. */
export type SemanticTokenKey = 'ink-muted';

/** Every key in `colorTokens`: the opaque cXX scale plus minted semantic roles. */
export type ColorTokenKey = CustomPaletteKey | SemanticTokenKey;

// New colors: add only when they occur 2+ times in the design (Figma) — mirrors
// the rule that used to live as a comment at the end of theme.ts's custom block.
// Minted semantic tokens (ink-*/surface-*) are gated by sourse/design/modeFlipAudit.ts.
export const colorTokens: Record<ColorTokenKey, ByMode<string>> = {
    c1: { light: '#FAFAFA', dark: '#FAFAFA' },
    c2: { light: '#FFFFFF', dark: '#D4D4D8' },
    c3: { light: '#3F3F46', dark: '#3F3F46' },
    c4: { light: '#D4D4D8', dark: '#71717A' },
    c5: { light: '#E4E4E7', dark: '#52525B' },
    c6: { light: '#14B8A6', dark: '#14B8A6' },
    c7: { light: '#F4F4F5', dark: '#27272A' },
    c8: { light: '#643EB0', dark: '#643EB0' },
    c9: { light: '#FAFAFA', dark: '#D9D9D9' },
    c10: { light: '#FF4C51', dark: '#EF5349' },
    c11: { light: '#FAFAFA', dark: '#E4E4E7' },
    c12: { light: '#8A99AF', dark: '#151519' },
    c13: { light: '#D97706', dark: '#D97706' },
    c14: { light: '#2563EB', dark: '#2563EB' },
    c15: { light: '#22D3EE', dark: '#22D3EE' },
    c16: { light: '#27272A', dark: '#F4F4F5' },
    c17: { light: '#282830', dark: '#282830' },
    c18: { light: '#191921', dark: '#C7CBE0' },
    c19: { light: '#FAFAFA', dark: '#8E8FA7' },
    c20: { light: '#EDEDF5', dark: '#CECAF9' },
    c21: { light: '#0500FF', dark: '#0500FF' },
    c22: { light: '#596C83', dark: '#FFFFFF' },
    c23: { light: '#476385', dark: '#191921' },
    c24: { light: '#485162', dark: '#16161B' },
    c25: { light: '#2C8354', dark: '#191921' },
    c26: { light: '#16A34A', dark: '#191921' },
    c27: { light: '#71717A', dark: '#191921' },
    c28: { light: '#ACB8C9', dark: '#191921' },
    c29: { light: '#191921', dark: '#726C7E' },
    c30: { light: '#191921', dark: '#191921' },
    c31: { light: '#52DD84', dark: '#191921' },
    c32: { light: '#E4E8EF', dark: '#CECAF9' },
    c33: { light: '#C6D2DF', dark: '#191921' },
    c34: { light: '#CECAF9', dark: '#3F3F46' },
    c35: { light: '#D1D9E7', dark: '#D1D9E7' },
    c36: { light: '#485162', dark: '#485162' },
    c37: { light: '#1F1E26', dark: '#1F1E26' },
    c38: { light: '#F9CB34', dark: '#F9CB34' },
    c39: { light: '#E85921', dark: '#E85921' },
    c40: { light: '#596C83', dark: '#596C83' },
    c41: { light: '#DCE2EB', dark: '#15151A' },
    c42: { light: '#15151A', dark: '#15151A' },
    c43: { light: '#232324', dark: '#232324' },
    c44: { light: '#CECAF90A', dark: '#CECAF90A' },
    c45: { light: '#71717A', dark: '#71717A' },
    c46: { light: '#E4E4E7', dark: '#52525B' },
    c47: { light: '#52525B', dark: '#52525B' },
    c48: { light: '#9AA8BA', dark: '#9AA8BA' },
    c49: { light: '#070709', dark: '#070709' },
    c50: { light: '#363543', dark: '#363543' },
    c51: { light: '#1b1a21', dark: '#1b1a21' },
    c52: { light: '#F97316', dark: '#F97316' },
    c53: { light: '#FFBE5B', dark: '#FFBE5B' },
    c54: { light: '#131216', dark: '#131216' },
    c55: { light: '#F7F6FE', dark: '#F7F6FE' },
    c56: { light: '#000000', dark: '#D4D4D8' }, // Primary body text (readable on both modes)
    c57: { light: '#485162', dark: '#71717A' }, // Secondary/muted text (darker for light blue backgrounds)
    c58: { light: '#334155', dark: '#CAD3E2' }, // Act border/title
    c59: { light: '#D7E4F5', dark: '#71717A' }, // Scene border
    c60: { light: '#A5B3C5', dark: '#070709' }, // Scene background
    c61: { light: '#DCE2EB', dark: '#25242E' }, // Act/Episode background base
    c62: { light: '#476385', dark: '#8092A9' }, // Episode border
    c63: { light: '#FFFFFF', dark: '#121217' }, // Card active background
    c64: { light: '#576C8B', dark: '#808997' }, // Act active border
    c65: { light: '#7896BA', dark: '#B2C3D8' }, // Episode active border
    c66: { light: '#B2C3D8', dark: '#B2C3D8' }, // Scene active border (both modes)
    c67: { light: '#8393AA', dark: '#71717A' }, // Scene header text hover/active/focus
    c68: { light: '#374151', dark: '#A1A1AA' }, // Table logline text
    c69: { light: '#121217', dark: '#FAFAFA' }, // Table plot text
    // Cards view (issue #1799); dark values provisional — dark Figma pending
    c70: { light: '#1E293B', dark: '#0B1220' }, // Act card header strip
    c71: { light: '#8B99AE', dark: '#3F4B5E' }, // Episode card header strip
    c72: { light: '#E5E9F0', dark: '#25242E' }, // Scene card header strip
    c73: { light: '#F9FAFB', dark: '#121217' }, // Card body background (Tailwind gray-50)
    c74: { light: '#F3F4F6', dark: '#070709' }, // Cards board background (Tailwind gray-100)
    c75: { light: '#FFFFFF', dark: '#E5E9F0' }, // Text on dark header strips
    c76: { light: '#C4DAFC', dark: '#2C3E5C' }, // Selected scene card header strip
    c77: { light: '#F0F6FE', dark: '#16202F' }, // Selected scene card body
    c78: { light: '#030712', dark: '#FFFFFF' }, // Heading text override (color="primary" is invisible in light mode; issue #1799 redesign)
    c79: { light: '#62748E', dark: '#71717A' }, // Secondary/muted heading text (issue #1799 redesign)
    c80: { light: '#EA580C', dark: '#EA580C' }, // «Союзник антагониста» role chip — orange per mockup, near the antagonist amber (characters redesign)

    // ─── Minted semantic tokens (ADR 0018 mode-flip audit — never a new cXX) ───
    'ink-muted': { light: '#71717A', dark: '#A1A1AA' }, // muted body ink; absorbs divergent {#71717A,#A1A1AA} ×16 (2026-07-25)
};

/**
 * The named MUI palette groups theme.ts customizes (primary/secondary/error/
 * info/success/text/background). Keyed by facet first, then mode — mirrors how
 * each facet becomes one CSS custom property (`--color-<group>[-<facet>]`) with
 * an optional `.dark` override, and matches MUI's own PaletteColor shape
 * (`main`/`light`/`dark`/`contrastText`) per group.
 *
 * `warning` is intentionally absent: theme.ts never overrides it (MUI's default
 * applies), so it isn't part of this duplication problem — tailwind.css's
 * `--color-warning` stays hand-authored, same as the editor-only tokens.
 */
export const semanticPalette = {
    primary: {
        main: { light: '#FFFFFF', dark: '#FFFFFF' },
        light: { light: '#54555F', dark: '#54555F' },
        contrastText: { light: '#E4E4E7', dark: '#A1A1AA' },
    },
    secondary: {
        main: { light: '#EE5B5B', dark: '#EE5B5B' },
        dark: { light: '#070609', dark: '#070609' },
        light: { light: '#FAFAFA', dark: '#CECAF90A' },
        contrastText: { light: '#FAFAFA', dark: '#8E8FA733' },
    },
    error: {
        main: { light: '#DA2C55', dark: '#DA2C55' },
        contrastText: { light: '#FFFFFF', dark: '#C7CBE066' },
    },
    info: {
        main: { light: '#9893A5', dark: '#9893A5' },
        light: { light: '#BDBAC5', dark: '#BDBAC5' },
        dark: { light: '#373642', dark: '#373642' },
        contrastText: { light: '#A1A1AA', dark: '#A1A1AA' },
    },
    success: {
        main: { light: '#35C075', dark: '#35C075' },
    },
    text: {
        primary: { light: '#52525B', dark: '#C7CBE099' },
        secondary: { light: '#FAFAFA', dark: '#C7CBE066' },
        disabled: { light: '#C7CBE066', dark: '#C7CBE066' },
    },
    background: {
        default: { light: '#F3F4F6', dark: '#070709' }, // Tailwind gray-100 — app-wide page bg (issue #1799 redesign)
        paper: { light: '#FFFFFF', dark: '#15151A' },
    },
} as const satisfies Record<string, Record<string, ByMode<string>>>;

export type SemanticPaletteGroup = keyof typeof semanticPalette;
